/**
 * useUrlCaptures — manage a set of landing-page URL captures that load in
 * PARALLEL (up to `max`). Each capture starts immediately and resolves to a
 * ready screenshot or an error. Used by the study intake and the chat attach
 * modal: type a URL → a loading chip appears → it fills in with the screenshot
 * → click to scroll / redo / validate.
 *
 * A capture is started from an effect that reacts to `loading` entries (guarded
 * by `startedRef` so each fires exactly once). Firing the fetch from the effect
 * — rather than synchronously inside `addUrl` — avoids a race where the request
 * is dropped because React has not yet committed the new capture to state.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  isYoutubeUrl,
  canonicalYoutubeWatchUrl,
  isDirectVideoUrl,
  nonKeyframeableVideoMessage,
  pageCaptureLineCode,
  refusedCaptureLineCode,
  unreadableVideoHostMessage,
} from '@digital-twins/shared-types'

import { ruleCopy } from '@/features/studies/questionnaire/utils/questionnaireIssueCopy'
import type { UrlCaptureResult } from '@/lib/api'
import { CAPTURE_BLANK_THRESHOLD, measureBlankFraction } from '@/lib/utils/compress-image'

export type UrlCaptureStatus = 'loading' | 'ready' | 'error'

export interface UrlCapture {
  id: string
  url: string
  status: UrlCaptureStatus
  result?: UrlCaptureResult
  error?: string
  validated?: boolean
  /** Soft quality flag: the screenshot looks mostly blank / likely botched. */
  warning?: 'looks_blank'
}

/** Accept bare domains ("nba.com") by defaulting to https. */
function normalizeUrl(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

/**
 * Human-readable reason for a failed capture, from the backend failureReason:
 * the rule catalog's line for it (`pageCaptureLineCode`), in the interface
 * language, or in `lng` for a line the chat's thread shows. The one owner of
 * that line: a saved page with no capture says it on its card and the chat
 * reply reads it (wave E L-CAP). A link to a video host has its own line,
 * `detail`, which names the host.
 */
export function friendlyCaptureError(
  reason: string | null | undefined,
  detail: string | null | undefined,
  lng?: string,
): string {
  if (reason === 'unreadable_video_host') {
    // The fallback only fires if a caller forgets to pass `detail`.
    return detail || "We can't read that video host. Upload the file instead."
  }
  return ruleCopy(pageCaptureLineCode(reason), undefined, lng) as string
}

/**
 * The reason for a capture that added nothing (an ad's failed capture): the
 * same cause as `friendlyCaptureError`, told without the exit that removes a
 * page, because there is none in the study. The line of a page that stays in
 * the study, as its link, is `friendlyCaptureError`.
 */
export function refusedCaptureError(
  reason: string | null | undefined,
  detail: string | null | undefined,
  lng?: string,
): string {
  if (reason === 'unreadable_video_host') return friendlyCaptureError(reason, detail, lng)
  return ruleCopy(refusedCaptureLineCode(reason), undefined, lng) as string
}

/** A page capture on the caller's own HTTP client (the builder's or the app's). */
export type CaptureUrl = (
  url: string,
  opts: { scale?: number; fullPage?: boolean },
) => Promise<UrlCaptureResult>

export function useUrlCaptures(opts: {
  capture: CaptureUrl
  scale?: number
  max?: number
  /**
   * Surface-specific sentence appended to the unreadable-video-host refusal.
   */
  unreadableHostHint?: string
  /**
   * Twin chat can still attach a YouTube watch URL. Questionnaire study intake
   * must pass false: twins need keyframes we cannot extract from YouTube.
   */
  allowYoutubeVideo?: boolean
  /**
   * A page whose capture fails stays in the study as its link
   * (`savesCaptureOfLink`), so its failure is the in-study line; any other
   * family's failed capture adds nothing and says the refusal.
   */
  keepsFailedLink?: boolean
}) {
  const capture = opts.capture
  const scale = opts.scale ?? 1
  const max = opts.max ?? 6
  const unreadableHostHint = opts.unreadableHostHint
  const allowYoutubeVideo = opts.allowYoutubeVideo ?? true
  const failureLine = opts.keepsFailedLink ? friendlyCaptureError : refusedCaptureError
  const [captures, setCaptures] = useState<Array<UrlCapture>>([])
  const seq = useRef(0)
  /** Ids whose capture has already been kicked off (dedupes the start effect). */
  const startedRef = useRef<Set<string>>(new Set())
  /**
   * Per-id run token. redo() reuses the same id, so a stale in-flight run could
   * otherwise resolve late and clobber the retry (flip it back to error, or
   * re-stamp a blank warning). Every state write is gated on the run still being
   * the current one for its id.
   */
  const runTokenRef = useRef<Map<string, number>>(new Map())

  const run = useCallback(
    async (id: string, url: string, token: number) => {
      const isCurrent = () => runTokenRef.current.get(id) === token
      try {
        // Checked FIRST, before every other branch. A Vimeo / Drive / Dropbox
        // link screenshots as a still of a player page: the video read then
        // finds nothing and the questionnaire is authored blind, with no error
        // anywhere. Refusing it with a written reason is the whole point.
        const blocked = allowYoutubeVideo
          ? unreadableVideoHostMessage(url)
          : nonKeyframeableVideoMessage(url)
        const unreadable = blocked && unreadableHostHint ? `${blocked} ${unreadableHostHint}` : blocked
        if (unreadable) {
          if (!isCurrent()) return
          setCaptures((cs) =>
            cs.map((c) =>
              c.id === id
                ? {
                    ...c,
                    status: 'error',
                    error: friendlyCaptureError('unreadable_video_host', unreadable),
                  }
                : c,
            ),
          )
          return
        }
        // A URL that points straight at video bytes attaches as the video
        // itself; there is no page here worth screenshotting.
        if (isDirectVideoUrl(url)) {
          if (!isCurrent()) return
          setCaptures((cs) =>
            cs.map((c) =>
              c.id === id
                ? {
                    ...c,
                    status: 'ready',
                    result: {
                      ok: true,
                      publicUrl: url,
                      pageTitle: 'Video',
                      source: 'video',
                    },
                  }
                : c,
            ),
          )
          return
        }
        if (allowYoutubeVideo && isYoutubeUrl(url)) {
          if (!isCurrent()) return
          const canonical = canonicalYoutubeWatchUrl(url) || url
          setCaptures((cs) =>
            cs.map((c) =>
              c.id === id
                ? {
                    ...c,
                    url: canonical,
                    status: 'ready',
                    result: {
                      ok: true,
                      publicUrl: canonical,
                      pageTitle: 'YouTube video',
                      source: 'youtube',
                    },
                  }
                : c,
            ),
          )
          return
        }
        const result = await capture(url, { scale, fullPage: true })
        if (!isCurrent()) return
        const ok = result.ok && (result.publicUrl || result.screenshotBase64)
        if (!ok) {
          setCaptures((cs) =>
            cs.map((c) =>
              c.id === id
                ? {
                    ...c,
                    status: 'error',
                    error: failureLine(result.failureReason, result.detail),
                  }
                : c,
            ),
          )
          return
        }
        setCaptures((cs) => cs.map((c) => (c.id === id ? { ...c, status: 'ready', result } : c)))

        // Best-effort: flag a mostly-blank screenshot so a botched extraction is
        // not silently presented as a good capture. Skip the flag when the page
        // has real extracted text (a content-rich dark/minimalist page can be
        // visually near-uniform without being a failed render).
        const hasText = (result.extractedText?.trim().length ?? 0) >= 200
        if (result.screenshotBase64 && !hasText) {
          try {
            const blankFraction = await measureBlankFraction(result.screenshotBase64)
            if (!isCurrent()) return
            if (blankFraction >= CAPTURE_BLANK_THRESHOLD) {
              setCaptures((cs) => cs.map((c) => (c.id === id ? { ...c, warning: 'looks_blank' } : c)))
            }
          } catch {
            /* analysis is best-effort; ignore failures */
          }
        }
      } catch (e) {
        if (!isCurrent()) return
        setCaptures((cs) =>
          cs.map((c) =>
            c.id === id
              ? { ...c, status: 'error', error: e instanceof Error ? e.message : 'Capture failed' }
              : c,
          ),
        )
      }
    },
    [capture, scale, unreadableHostHint, allowYoutubeVideo, failureLine],
  )

  // Start every loading capture exactly once, after it is committed to state.
  useEffect(() => {
    for (const c of captures) {
      if (c.status === 'loading' && !startedRef.current.has(c.id)) {
        startedRef.current.add(c.id)
        const token = (runTokenRef.current.get(c.id) ?? 0) + 1
        runTokenRef.current.set(c.id, token)
        void run(c.id, c.url, token)
      }
    }
  }, [captures, run])

  /**
   * Stage a URL and return the id it will carry, so a caller can follow that one
   * capture to ready/error (the composer submits as soon as the URL it just
   * added lands). Null when there was nothing to add. The id is returned even if
   * the cap rejected the entry: no capture then exists under it, so a lookup
   * simply finds nothing.
   */
  const addUrl = useCallback(
    (rawUrl: string): string | null => {
      const url = normalizeUrl(rawUrl)
      if (!url) return null
      const id = `cap_${Date.now()}_${seq.current++}`
      // A failed capture is no asset: it holds no place under `max`.
      setCaptures((cs) =>
        cs.filter((c) => c.status !== 'error').length >= max
          ? cs
          : [...cs, { id, url, status: 'loading' }],
      )
      return id
    },
    [max],
  )

  const redo = useCallback(
    (id: string) => {
      // Invalidate the in-flight run NOW — not only when the start effect re-fires.
      // Otherwise a slow original run that resolves in the window between this
      // setState and the effect could still pass its token check and clobber the
      // retry (flip it back to error / re-stamp a stale blank warning).
      runTokenRef.current.set(id, (runTokenRef.current.get(id) ?? 0) + 1)
      // Allow the start effect to fire again for this id.
      startedRef.current.delete(id)
      setCaptures((cs) => {
        // A failed capture retried takes a place again, so only while one is free.
        const retried = cs.find((c) => c.id === id)
        const held = cs.filter((c) => c.status !== 'error').length
        if (retried?.status === 'error' && held >= max) return cs
        return cs.map((c) =>
          c.id === id
            ? { ...c, status: 'loading', error: undefined, validated: false, warning: undefined }
            : c,
        )
      })
    },
    [max],
  )

  const remove = useCallback((id: string) => {
    startedRef.current.delete(id)
    runTokenRef.current.delete(id)
    setCaptures((cs) => cs.filter((c) => c.id !== id))
  }, [])

  const validate = useCallback(
    (id: string) => setCaptures((cs) => cs.map((c) => (c.id === id ? { ...c, validated: true } : c))),
    [],
  )

  const clear = useCallback(() => {
    startedRef.current.clear()
    runTokenRef.current.clear()
    setCaptures([])
  }, [])

  return { captures, addUrl, redo, remove, validate, clear, count: captures.length }
}

/** Canonical watch URL when this capture is a YouTube video, else null. */
export function youtubeUrlFromCapture(capture: UrlCapture): string | null {
  if (capture.status !== 'ready') return null
  if (capture.result?.source === 'youtube') {
    return canonicalYoutubeWatchUrl(capture.result.publicUrl || capture.url)
  }
  return canonicalYoutubeWatchUrl(capture.result?.publicUrl || capture.url)
}

/** The video file URL when this capture is a direct video file, else null. */
export function videoUrlFromCapture(capture: UrlCapture): string | null {
  if (capture.status !== 'ready') return null
  if (capture.result?.source !== 'video') return null
  const url = capture.result.publicUrl || capture.url
  return isDirectVideoUrl(url) ? url : null
}
